import MonashConnectionResearch from "@/components/site/MonashConnectionResearch";
import { Link } from "react-router-dom";
import {
  ArrowRight, Users, Heart, Activity, Brain, BarChart3, Globe,
  HeartHandshake, User, Dumbbell, Briefcase, Landmark, Sparkles, Check,
  Smile, LifeBuoy, Handshake, Quote, BookOpen, ExternalLink,
} from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import Photo from "@/components/site/Photo";
import SectionIntro from "@/components/site/SectionIntro";
import ClosingCta from "@/components/site/ClosingCta";
import ConnectionData from "@/components/site/ConnectionData";
import useSeo from "@/hooks/useSeo";

const uncertaintyStudies = [
  {
    title: "The Shared and Distinct Mechanisms Underlying Fear of Evaluation in Social Anxiety",
    ref: "Gao et al., 2025",
    desc: "Examines how fear of negative evaluation operates in social anxiety.",
    url: "https://pubmed.ncbi.nlm.nih.gov/41036168/",
  },
  {
    title: "Cognitive-Emotional Impact of Negative Interpretations of Ambiguous Social Information and Fear of Negative Evaluation",
    ref: "Spiroiu & Maranzan, 2025",
    desc: "Examined links between intolerance of uncertainty, fear of negative evaluation, interpretations of ambiguous social situations and social anxiety.",
    url: "https://pubmed.ncbi.nlm.nih.gov/40425265/",
  },
  {
    title: "Adapting to uncertainty: The role of anxiety and fear of negative evaluation in learning in social and non-social contexts",
    ref: "Topel et al., 2024",
    desc: "Studied how anxiety and fear of negative evaluation shape how people respond to social uncertainty.",
    url: "https://pubmed.ncbi.nlm.nih.gov/39043306/",
  },
];

const sourceGroups = [
  {
    title: "Social Connection & Loneliness",
    items: [
      { title: "From loneliness to social connection: charting a path to healthier societies", org: "World Health Organization — Commission on Social Connection", year: "2025", desc: "The WHO Commission's flagship report on loneliness, social isolation and social connection worldwide.", url: "https://www.who.int/groups/commission-on-social-connection" },
      { title: "Social isolation and loneliness", org: "Australian Institute of Health and Welfare", year: "2025", desc: "Australian statistics on loneliness and social isolation, using HILDA Survey data (waves 1–24).", url: "https://www.aihw.gov.au/reports/australias-welfare/social-isolation-and-loneliness" },
      { title: "Social cohesion and social connection", org: "Australian Institute of Health and Welfare", year: "2025", desc: "How connection, belonging and community participation are tracked across Australia.", url: "https://www.aihw.gov.au/reports/australias-welfare/social-cohesion-and-social-connection" },
      { title: "Social relationships and mortality risk: A meta-analytic review", org: "Holt-Lunstad, Smith & Layton", year: "2010", desc: "Landmark meta-analysis linking stronger social relationships with improved survival.", url: "https://doi.org/10.1371/journal.pmed.1000316" },
      { title: "Our epidemic of loneliness and isolation: The U.S. Surgeon General's advisory", org: "U.S. Department of Health & Human Services", year: "2023", desc: "The U.S. Surgeon General's advisory on loneliness, isolation and connection.", url: "https://www.hhs.gov/surgeongeneral/priorities/loneliness/index.html" },
    ],
  },
  {
    title: "Social Uncertainty & Evaluation",
    items: [
      { title: "The Shared and Distinct Mechanisms Underlying Fear of Evaluation in Social Anxiety", org: "Gao et al.", year: "2025", desc: "Examines how fear of negative evaluation operates in social anxiety.", url: "https://pubmed.ncbi.nlm.nih.gov/41036168/" },
      { title: "Cognitive-Emotional Impact of Negative Interpretations of Ambiguous Social Information and Fear of Negative Evaluation", org: "Spiroiu & Maranzan", year: "2025", desc: "Links between intolerance of uncertainty, fear of negative evaluation, ambiguous social situations and social anxiety.", url: "https://pubmed.ncbi.nlm.nih.gov/40425265/" },
      { title: "Adapting to uncertainty: anxiety and fear of negative evaluation in social and non-social learning", org: "Topel et al.", year: "2024", desc: "How anxiety and fear of negative evaluation shape responses to social uncertainty.", url: "https://pubmed.ncbi.nlm.nih.gov/39043306/" },
      { title: "Measurement of social-evaluative anxiety", org: "Watson & Friend", year: "1969", desc: "The foundational research behind the Fear of Negative Evaluation scale.", url: "https://pubmed.ncbi.nlm.nih.gov/5810590/" },
      { title: "It's not just the judgements — it's that I don't know: Intolerance of uncertainty as a predictor of social anxiety", org: "Carleton, Collimore & Asmundson", year: "2010", desc: "Found intolerance of uncertainty predicts social anxiety.", url: "https://doi.org/10.1016/j.janxdis.2009.10.007" },
      { title: "Information-processing bias in social phobia", org: "Hirsch & Clark", year: "2004", desc: "Review of how ambiguous social situations can be interpreted more negatively.", url: "https://doi.org/10.1016/j.cpr.2004.07.005" },
    ],
  },
  {
    title: "Social Relationships & Stress",
    items: [
      { title: "Recent Developments in the Study of Social Relationships, Stress Responses, and Physical Health", org: "Hostinar", year: "2015", desc: "Reviews social buffering — how supportive relationships can influence stress responses and health.", url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4562328/" },
      { title: "Stress, social support, and the buffering hypothesis", org: "Cohen & Wills", year: "1985", desc: "The classic paper on how social support can buffer responses to stress.", url: "https://doi.org/10.1037/0033-2909.98.2.310" },
      { title: "The need to belong: Desire for interpersonal attachments as a fundamental human motivation", org: "Baumeister & Leary", year: "1995", desc: "The influential case that belonging is a fundamental human motivation.", url: "https://doi.org/10.1037/0033-2909.117.3.497" },
    ],
  },
];

const bigPicture = [
  { icon: Users, tone: "teal", value: "1 in 6", caption: "people globally report loneliness.", source: "WHO 2025" },
  { icon: Heart, tone: "orange", value: "~871,000", caption: "deaths per year linked to loneliness.", source: "WHO 2025" },
  { icon: Activity, tone: "teal", value: "+29%", caption: "heart disease risk linked to poor social connection.", source: "U.S. Surgeon General 2023" },
  { icon: Brain, tone: "orange", value: "+32%", caption: "stroke risk linked to poor social connection.", source: "U.S. Surgeon General 2023" },
  { icon: BarChart3, tone: "teal", value: "+50%", caption: "greater likelihood of survival with stronger social relationships.", source: "Holt-Lunstad et al., PLOS Medicine 2010" },
];

const riskBars = [
  { label: "Heart disease", pct: 29, tone: "bg-teal", text: "text-teal" },
  { label: "Stroke", pct: 32, tone: "bg-orange", text: "text-orange" },
  { label: "Premature mortality", pct: 29, tone: "bg-teal", text: "text-teal" },
];

const benefits = [
  { icon: HeartHandshake, title: "Mental wellbeing", body: "Regular, meaningful contact with others is associated with a stronger sense of wellbeing." },
  { icon: Users, title: "Community belonging", body: "Feeling part of a local community is linked to a stronger sense of belonging." },
  { icon: User, title: "Confidence & participation", body: "Face-to-face interaction and community participation are linked to greater confidence." },
  { icon: Dumbbell, title: "Physical health", body: "Social connection is associated with lower health risks and greater longevity." },
  { icon: Briefcase, title: "Professional networking", body: "In-person connections can open doors to opportunities, mentorship and career growth." },
  { icon: Landmark, title: "Local community engagement", body: "Stronger social ties help create more vibrant, resilient and engaged communities." },
  { icon: Smile, title: "Friendship", body: "Shared moments and face-to-face conversations are where friendships begin and grow." },
  { icon: LifeBuoy, title: "Support", body: "Knowing the people around you — and being able to reach them — builds real support networks." },
  { icon: Handshake, title: "Reduced social isolation", body: "Regular connection helps reduce social isolation and strengthens ties to the people nearby." },
];

const orrbbitLinks = [
  "Meet people nearby based on shared intent",
  "Best Nearby Match surfaces people who may share your vibe, interests and intent",
  "Choose a public place to meet by mutual agreement",
  "Discover and attend local events",
  "Connect with your local community",
];

const notKnowingQuestions = [
  "Do they want to talk?",
  "Am I interrupting them?",
  "Are they open to meeting someone new?",
  "Will they reject me?",
  "Will they think I'm strange for saying hello?",
];

const gapQuestions = [
  "Are they busy?",
  "Are they interested?",
  "Did they notice me?",
  "Would they welcome a conversation?",
  "Should I leave them alone?",
];

const signals = ["Facial expressions", "Body language", "Eye contact", "Whether someone appears busy", "Whether they seem approachable"];

const overthinking = [
  "Should I say something?",
  "Did they look at me?",
  "Maybe they're busy.",
  "What if they don't want to talk?",
  "What if I make it awkward?",
];

const journey = [
  { title: "Discover", body: "See people nearby who have chosen to be visible and are open to connecting." },
  { title: "Request", body: "Send a consent-based connection request." },
  { title: "Mutual acceptance", body: "The other person chooses whether to accept. Nothing happens without mutual consent." },
  { title: "Location sharing", body: "Once both people agree to connect, the Orrbbit meetup experience unlocks and shared location becomes available between those two users." },
  { title: "Meet", body: "Use Orrbbit to locate one another and make the connection face-to-face." },
];

const connectionOutcomes = [
  "Friendship", "Belonging", "Confidence", "Community", "Support", "Advice",
  "Professional opportunity", "Collaboration", "Shared experiences", "New perspectives",
  "Local participation", "Networks", "Relationships",
];

const researchSummaries = [
  { title: "Fear of evaluation", body: "Research into social anxiety consistently identifies concern about negative evaluation and judgement from others as an important factor in social anxiety.", ref: "Watson & Friend, 1969" },
  { title: "Uncertainty", body: "Research has also examined how difficulty tolerating uncertainty can contribute to worry and anxiety, including in social situations.", ref: "Carleton, Collimore & Asmundson, 2010" },
  { title: "Ambiguous social situations", body: "Studies suggest people experiencing higher levels of social anxiety may interpret unclear social situations more negatively.", ref: "Hirsch & Clark, 2004" },
  { title: "Social support", body: "A large body of research has examined how supportive relationships can help buffer people's responses to stress.", ref: "Cohen & Wills, 1985" },
  { title: "Belonging", body: "Research across psychology and public health has consistently recognised social relationships and belonging as important aspects of human wellbeing.", ref: "Baumeister & Leary, 1995" },
];

const SourceLine = ({ children }) => (
  <div className="mt-3">
    <div className="h-0.5 w-5 bg-orange" />
    <p className="mt-1.5 text-[10px] font-medium text-bodygrey">{children}</p>
  </div>
);

const Highlight = ({ children, testid }) => (
  <Reveal className="mt-8">
    <div className="rounded-3xl border border-teal/20 bg-teal/5 p-7 text-center md:p-8" data-testid={testid}>
      <p className="mx-auto max-w-2xl font-heading text-lg font-semibold tracking-tight text-navy md:text-xl">{children}</p>
    </div>
  </Reveal>
);

export default function Benefits() {
  useSeo({
    title: "Benefits of Human Connection",
    description: "Research from the WHO and U.S. Surgeon General links social connection with better health, belonging and opportunity. See the evidence — and how Orrbbit helps people connect in the real world.",
    path: "/benefits-of-human-connection",
  });

  return (
    <div data-testid="benefits-page">
      {/* HERO */}
      <section className="bg-lightbg" data-testid="benefits-hero">
        <div className="container-x grid grid-cols-1 items-start gap-8 section-space lg:grid-cols-2 lg:gap-12">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">Benefits of Human Connection</span>
            <h1 className="mt-4 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-navy md:text-5xl xl:text-[3.4rem]">
              Technology should help people connect in the real world — not keep them behind a screen.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-bodygrey">
              Research shows that social connection is associated with better health, wellbeing, belonging and opportunity.
              Orrbbit is designed to help create more real-world connection opportunities — because a healthier,
              happier, more connected world is possible.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-started" data-testid="benefits-cta-primary" className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-semibold text-white shadow-soft transition-transform hover:scale-105">Get Started <ArrowRight size={18} /></Link>
              <Link to="/how-it-works" data-testid="benefits-cta-secondary" className="inline-flex items-center gap-2 rounded-full border-2 border-teal px-7 py-3.5 font-semibold text-teal transition-colors hover:bg-teal hover:text-white">Explore Orrbbit</Link>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <Photo slug="benefits-hero" ratio="aspect-[5/4]" priority rounded="rounded-[2rem]" className="shadow-card" data-testid="benefits-hero-photo" />
              {/* Handwritten overlay */}
              <div className="absolute left-5 top-5 -rotate-3 md:left-8 md:top-8" data-testid="benefits-hero-note">
                <p className="font-hand text-3xl leading-[0.95] text-navy md:text-[2.6rem]">Life happens</p>
                <p className="font-hand text-3xl leading-[0.95] text-navy md:text-[2.6rem]">better together</p>
                <svg viewBox="0 0 120 12" className="mt-1 h-3 w-28 text-teal" fill="none" aria-hidden="true">
                  <path d="M3 9 C 30 2, 60 2, 117 7" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" />
                </svg>
              </div>
              {/* Floating cards */}
              <div className="absolute right-4 top-8 flex rotate-2 items-center gap-2.5 rounded-2xl bg-white/95 px-4 py-3 shadow-card backdrop-blur md:right-6" data-testid="benefits-float-1">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-teal/10 text-teal"><Users size={17} /></span>
                <span>
                  <span className="block text-xs font-semibold text-navy">Stronger communities</span>
                  <span className="block text-[11px] text-bodygrey">happier people</span>
                </span>
              </div>
              <div className="absolute bottom-8 left-4 flex -rotate-2 items-center gap-2.5 rounded-2xl bg-white/95 px-4 py-3 shadow-card backdrop-blur md:left-6" data-testid="benefits-float-2">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-orange/10 text-orange"><Sparkles size={17} /></span>
                <span>
                  <span className="block text-xs font-semibold text-navy">Real connection</span>
                  <span className="block text-[11px] text-bodygrey">brighter tomorrows</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* S1 — THE STRESS OF NOT KNOWING */}
      <section className="bg-white" data-testid="benefits-stress-not-knowing">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">Why connection can feel hard</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">The Stress of Not Knowing</h2>
            <p className="mt-4 text-bodygrey">Human interaction often starts with uncertainty.</p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="not-knowing-questions">
            {notKnowingQuestions.map((q) => (
              <span key={q} className="inline-flex items-center gap-2 rounded-full bg-lightbg px-4 py-2 text-sm italic text-navy">
                <Quote size={13} className="flex-none text-teal" /> {q}
              </span>
            ))}
          </div>
          <Reveal className="mt-8 max-w-3xl">
            <p className="text-bodygrey">
              Sometimes the hardest part of starting a conversation isn't the conversation itself — it's not knowing
              how the other person might respond.
            </p>
            <p className="mt-3 text-bodygrey">
              For many people, uncertainty can create hesitation before an interaction even begins. Two people may both
              be open to meeting someone new, yet neither makes the first move because neither knows what the other
              person is thinking.
            </p>
          </Reveal>
          <Highlight testid="stress-highlight">Sometimes the barrier isn't rejection. It's not knowing.</Highlight>
          <Reveal className="mt-8 max-w-3xl">
            <div className="rounded-2xl border border-border bg-lightbg p-6" data-testid="research-behind-uncertainty">
              <p className="flex items-center gap-2 font-heading text-base font-semibold text-navy"><BookOpen size={16} className="text-teal" /> Research behind this</p>
              <p className="mt-2 text-sm text-bodygrey">
                Research into social anxiety has examined how fear of evaluation, uncertainty and the interpretation
                of ambiguous social situations can influence how people respond in social environments.
              </p>
              <ul className="mt-4 space-y-4">
                {uncertaintyStudies.map((s) => (
                  <li key={s.ref} className="rounded-xl bg-white p-4 shadow-soft">
                    <p className="text-sm font-semibold text-navy">{s.title}</p>
                    <p className="mt-0.5 text-xs font-medium text-bodygrey">{s.ref}</p>
                    <p className="mt-1.5 text-sm text-bodygrey">{s.desc}</p>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-teal hover:underline">
                      View study <ArrowRight size={14} />
                    </a>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] text-bodygrey">These studies examine human social behaviour generally — they did not test Orrbbit.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* S2 — WHAT WILL THEY THINK OF ME? */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-fear-evaluation">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">What Will They Think of Me?</h2>
            <p className="mt-4 text-bodygrey">
              Psychological research has long examined fear of negative evaluation — the concern that other people may
              judge, reject or think negatively about us.
            </p>
            <p className="mt-3 text-bodygrey">
              This can influence how comfortable people feel approaching someone new, speaking up or entering
              unfamiliar social situations.
            </p>
            <p className="mt-3 text-bodygrey">
              You don't need to have a social anxiety disorder to recognise the feeling. Most people have experienced
              some version of: <em>"What are they going to think of me?"</em>
            </p>
            <p className="mt-3 text-bodygrey">
              Like most human experiences, this exists on a spectrum — from the occasional nervous moment to feelings
              that are more persistent. Feeling unsure before saying hello doesn't mean something is wrong with you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* S3 — SOMETIMES UNCERTAINTY IS THE PROBLEM */}
      <section className="border-t border-border bg-white" data-testid="benefits-uncertainty">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Sometimes It's Not the Rejection — It's Not Knowing</h2>
            <p className="mt-4 text-bodygrey">
              Research also suggests that difficulty dealing with uncertainty can play a role in anxiety around social
              situations. When another person's intentions are unclear, our minds naturally try to fill in the missing
              information.
            </p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="gap-questions">
            {gapQuestions.map((q) => (
              <span key={q} className="inline-flex items-center gap-2 rounded-full bg-lightbg px-4 py-2 text-sm italic text-navy">
                <Quote size={13} className="flex-none text-teal" /> {q}
              </span>
            ))}
          </div>
          <Reveal className="mt-8 max-w-3xl">
            <p className="text-bodygrey">
              Without clear information, people can begin predicting what might happen before anything has actually
              happened. Research into intolerance of uncertainty, social anxiety and ambiguous social situations
              suggests uncertainty can contribute to worry and hesitation for some people — though everyone's
              experience is different.
            </p>
          </Reveal>
          <Highlight testid="uncertainty-highlight">The mind often tries to answer questions that the situation hasn't answered yet.</Highlight>
        </div>
      </section>

      {/* S4 — WHEN WE DON'T KNOW, WE FILL IN THE GAPS */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-fill-gaps">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">When We Don't Know, We Fill In the Gaps</h2>
            <p className="mt-4 text-bodygrey">People constantly interpret social signals.</p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="social-signals">
              {signals.map((s) => (
                <span key={s} className="inline-flex rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-navy shadow-soft">{s}</span>
              ))}
            </div>
            <p className="mt-5 text-bodygrey">
              But those signals are not always easy to read. Someone may look uninterested when they are simply shy.
              Someone may be sitting alone and genuinely hoping someone talks to them. Someone else may want to meet
              people but be worried about making the first move.
            </p>
            <p className="mt-3 text-bodygrey">
              When intentions remain invisible, two people who may have been happy to connect can simply walk past
              each other.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="fill-gaps-scenario">
            <Reveal>
              <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-soft">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal">Person A</p>
                <p className="mt-2 text-sm italic text-navy">"I'd be happy to talk to someone, but I don't want to bother anyone."</p>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="h-full rounded-2xl border border-border bg-white p-6 shadow-soft">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal">Person B</p>
                <p className="mt-2 text-sm italic text-navy">"I'd like to meet someone, but I don't know if they want to talk."</p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full rounded-2xl border border-orange/25 bg-orange/5 p-6 shadow-soft">
                <p className="text-xs font-semibold uppercase tracking-wider text-orange">Result</p>
                <p className="mt-2 text-sm font-semibold text-navy">Neither person approaches.</p>
              </div>
            </Reveal>
          </div>
          <Highlight testid="fill-gaps-highlight">What if they already knew?</Highlight>
        </div>
      </section>

      {/* S5 — MAKING INTENTION VISIBLE */}
      <section className="border-t border-border bg-white" data-testid="benefits-intention-visible">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">The Orrbbit approach</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Making Intention Visible</h2>
            <p className="mt-4 text-bodygrey">
              Orrbbit is designed to reduce some of the uncertainty that exists before a conversation even starts.
              Instead of trying to guess whether somebody nearby is open to connecting, Orrbbit allows people to
              intentionally signal what they are open to.
            </p>
            <p className="mt-3 text-bodygrey">
              That might be meeting new people, networking, getting advice, joining an activity or making another type
              of real-world connection.
            </p>
            <p className="mt-3 text-bodygrey">
              It doesn't guarantee that two people will connect. It doesn't guarantee compatibility. But it can answer
              one of the first questions people often have: <strong className="text-navy">Are they actually open to
              meeting someone?</strong>
            </p>
          </Reveal>
          <Highlight testid="intention-highlight">Orrbbit turns invisible intention into a visible signal.</Highlight>
        </div>
      </section>

      {/* S6 — FROM COLD APPROACH TO MUTUAL CHOICE */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-mutual-choice">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">From Cold Approach to Mutual Choice</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-border bg-white p-7 shadow-soft" data-testid="cold-approach-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-bodygrey">The traditional situation</p>
                <ul className="mt-4 space-y-3">
                  {[
                    "You notice somebody.",
                    "You don't know if they want to talk.",
                    "You don't know whether you're interrupting them.",
                    "You approach them.",
                    "You find out their interest only after taking that social risk.",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-bodygrey"><Quote size={14} className="mt-0.5 flex-none text-bodygrey" /> {t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-3xl border border-teal/30 bg-teal/5 p-7" data-testid="mutual-choice-card">
                <p className="text-xs font-semibold uppercase tracking-wider text-teal">Orrbbit People Mode</p>
                <ul className="mt-4 space-y-3">
                  {[
                    "You see somebody nearby who has chosen to be visible and open to connection.",
                    "You send a connection request.",
                    "They choose privately whether to accept or decline.",
                    "Only when both people agree does the meetup experience unlock.",
                    "Their shared locations then become available to each other so they can find one another and meet.",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-navy"><Check size={14} className="mt-0.5 flex-none text-teal" /> {t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Highlight testid="mutual-choice-highlight">The first face-to-face interaction starts with mutual intent — not a guess.</Highlight>
        </div>
      </section>

      {/* S7 — THE PEOPLE MODE JOURNEY */}
      <section className="border-t border-border bg-white" data-testid="benefits-people-journey">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">How People Mode works</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">From nearby to face-to-face</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="benefits-journey-steps">
            {journey.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-border bg-lightbg p-6 shadow-soft" data-testid={`benefits-journey-step-${i}`}>
                  <div className="text-xs font-semibold text-orange">STEP {i + 1}</div>
                  <h3 className="mt-1 font-heading text-lg font-semibold text-navy">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-bodygrey">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Highlight testid="journey-highlight">Discover. Connect. Meet.</Highlight>
        </div>
      </section>

      {/* S8 — LESS GUESSING. LESS OVERTHINKING. */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-less-guessing">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Less Guessing. Less Overthinking.</h2>
            <p className="mt-4 text-bodygrey">
              When another person's intentions are unclear, it's easy to start analysing the situation.
            </p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="overthinking-questions">
            {overthinking.map((q) => (
              <span key={q} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm italic text-navy shadow-soft">
                <Quote size={13} className="flex-none text-teal" /> {q}
              </span>
            ))}
          </div>
          <Reveal className="mt-8 max-w-3xl">
            <p className="text-bodygrey">
              Orrbbit cannot remove every uncertainty from human interaction — nor should it. But it can make the first
              step clearer. Instead of wondering whether someone nearby is open to connection, Orrbbit allows them to
              tell you.
            </p>
          </Reveal>
          <Highlight testid="less-guessing-highlight">Less guessing. More opportunity to connect.</Highlight>
        </div>
      </section>

      {/* S9 — REDUCING THE UNCERTAINTY AROUND REJECTION */}
      <section className="border-t border-border bg-white" data-testid="benefits-rejection">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Reducing the Uncertainty Around Rejection</h2>
            <p className="mt-4 text-bodygrey">
              Rejection is a normal part of human interaction. Orrbbit isn't designed to eliminate it.
            </p>
            <p className="mt-3 text-bodygrey">
              Instead, the platform changes <em>when</em> that decision happens. Rather than walking up to someone
              without knowing whether they want to meet, a connection request allows both people to make that choice
              first.
            </p>
            <p className="mt-3 text-bodygrey">
              If they accept, both people already know something important before meeting:
              <strong className="text-navy"> we both chose this connection.</strong>
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE DATA BEHIND HUMAN CONNECTION — WHO + AIHW visuals */}
      <MonashConnectionResearch />

      <ConnectionData />

      {/* S10 — CONNECTION AND STRESS */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-stress-support">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Connection Can Help Us Handle Stress</h2>
            <p className="mt-4 text-bodygrey">
              Human connection is about more than meeting people. Researchers have also studied how supportive social
              relationships can influence the way people experience and respond to stress.
            </p>
            <p className="mt-3 text-bodygrey">
              Social support has been associated with psychological and physiological benefits across a range of
              contexts. Strong relationships can provide reassurance, belonging, perspective and practical support
              during difficult periods.
            </p>
            <p className="mt-3 text-bodygrey">
              Researchers have also studied the idea of "social buffering" — the ways supportive relationships can
              influence our response to stressful experiences.{" "}
              <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC4562328/" target="_blank" rel="noopener noreferrer" className="font-semibold text-teal hover:underline" data-testid="social-buffering-link">
                View research (Hostinar, 2015) →
              </a>
            </p>
            <p className="mt-3 text-sm text-bodygrey">
              This research relates to human relationships and social support generally. Orrbbit's role is to help
              create opportunities for those relationships to begin.
            </p>
          </Reveal>
          <Highlight testid="stress-support-highlight">Orrbbit creates opportunities for connection. What grows from that connection belongs to the people involved.</Highlight>
        </div>
      </section>

      {/* EXISTING — THE BIG PICTURE */}
      <section className="bg-white" data-testid="big-picture">
        <div className="container-x grid grid-cols-1 items-start gap-10 section-space lg:grid-cols-[300px_1fr]">
          <SectionIntro
            eyebrow="The big picture"
            title="A more connected world is a healthier, happier world."
            body="These findings from recognised health institutions show why human connection matters."
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
            {bigPicture.map((s, i) => (
              <Reveal key={s.value + s.source} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-5 shadow-soft" data-testid={`stat-card-${i}`}>
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${s.tone === "orange" ? "bg-orange/10 text-orange" : "bg-teal/10 text-teal"}`}>
                    <s.icon size={18} />
                  </span>
                  <p className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy md:text-[1.65rem]">{s.value}</p>
                  <p className="mt-1 text-xs leading-snug text-bodygrey">{s.caption}</p>
                  <SourceLine>{s.source}</SourceLine>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Photo break: community */}
      <section className="container-x py-4 md:py-8" data-testid="benefits-photo-break-1">
        <Reveal>
          <Photo slug="community-table-older" ratio="aspect-[16/9] md:aspect-[21/9]" className="shadow-soft" />
        </Reveal>
      </section>

      {/* EXISTING — WHAT THE RESEARCH SHOWS */}
      <section className="bg-lightbg" data-testid="research-section">
        <div className="container-x grid grid-cols-1 items-start gap-10 section-space lg:grid-cols-[300px_1fr]">
          <SectionIntro
            eyebrow="What the research shows"
            title="Connection matters across health, life stages and around the world."
            body="These findings highlight the wide-ranging impact of social connection, from physical health risks to who is most affected by loneliness."
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Bar chart card */}
            <Reveal>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-soft" data-testid="research-bars">
                <h3 className="text-sm font-bold text-navy">Higher health risks with poor social connection</h3>
                <p className="mt-1 text-[11px] text-bodygrey">Increased risk compared to those with stronger social connection</p>
                <div className="mt-5 space-y-4">
                  {riskBars.map((b) => (
                    <div key={b.label}>
                      <div className="flex items-baseline justify-between text-[11px]">
                        <span className="font-medium text-navy">{b.label}</span>
                        <span className={`font-bold ${b.text}`}>+{b.pct}%</span>
                      </div>
                      <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-slate-100">
                        <div className={`h-full rounded-full ${b.tone}`} style={{ width: `${b.pct * 2.2}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <SourceLine>U.S. Surgeon General 2023</SourceLine>
              </div>
            </Reveal>
            {/* Donut card */}
            <Reveal delay={0.06}>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-soft" data-testid="research-donut">
                <h3 className="text-sm font-bold text-navy">1 in 6 people globally report loneliness</h3>
                <div className="mt-5 flex items-center gap-5">
                  <div className="relative h-32 w-32 flex-none rounded-full" style={{ background: "conic-gradient(#18B8B4 0 16.6%, #E3EEF2 16.6% 100%)" }} role="img" aria-label="Donut chart showing 16 percent">
                    <div className="absolute inset-[16%] flex items-center justify-center rounded-full bg-white">
                      <span className="font-heading text-xl font-bold text-navy">1 in 6</span>
                    </div>
                  </div>
                  <p className="text-xs text-bodygrey"><span className="block font-heading text-3xl font-bold text-teal">16%</span> of people worldwide report loneliness.</p>
                </div>
                <SourceLine>WHO 2025</SourceLine>
              </div>
            </Reveal>
            {/* Demographics card */}
            <Reveal delay={0.12}>
              <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-soft" data-testid="research-demographics">
                <h3 className="text-sm font-bold text-navy">Highest rates among young people and in lower-income countries</h3>
                <div className="mt-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-teal/10 text-teal"><Users size={17} /></span>
                    <p className="text-xs text-bodygrey"><span className="font-heading text-base font-bold text-navy">~1 in 5</span><br />adolescents and young adults report loneliness.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-teal/10 text-teal"><Globe size={17} /></span>
                    <p className="text-xs text-bodygrey"><span className="font-heading text-base font-bold text-navy">~1 in 4</span><br />people in lower-income countries report loneliness.</p>
                  </div>
                </div>
                <SourceLine>WHO 2025</SourceLine>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.05} className="lg:col-start-2">
            <p className="text-[11px] leading-relaxed text-bodygrey" data-testid="benefits-sources">
              Sources: World Health Organization Commission on Social Connection (who.int, 2025); U.S. Department of Health &amp; Human Services / Office of the Surgeon General (hhs.gov, 2023); Holt-Lunstad, Smith &amp; Layton, PLOS Medicine (2010). Figures are associations reported in the cited research — not medical advice or treatment claims.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Photo break: face-to-face */}
      <section className="container-x py-4 md:py-8" data-testid="benefits-photo-break-2">
        <Reveal>
          <Photo slug="outdoor-gathering-two" ratio="aspect-[16/9] md:aspect-[21/9]" className="shadow-soft" />
        </Reveal>
      </section>

      {/* S11 — CONNECTION CREATES MORE THAN CONVERSATION */}
      <section className="bg-white" data-testid="benefits-more-than">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Connection Creates More Than Conversation</h2>
            <p className="mt-4 text-bodygrey">
              One conversation can become a friendship. One introduction can become a professional opportunity. One
              meetup can lead to a new community. One shared interest can introduce you to people you may never
              otherwise have met.
            </p>
            <p className="mt-3 font-semibold text-navy">The value of connection isn't always known at the beginning.</p>
          </Reveal>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="connection-outcomes">
            {connectionOutcomes.map((c) => (
              <span key={c} className="inline-flex rounded-full bg-teal/10 px-4 py-2 text-sm font-medium text-teal">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* EXISTING — THE MANY BENEFITS */}
      <section className="border-t border-border bg-white" data-testid="benefits-grid">
        <div className="container-x grid grid-cols-1 items-start gap-10 section-space lg:grid-cols-[300px_1fr]">
          <SectionIntro
            eyebrow="The many benefits"
            title="Stronger people. Stronger communities."
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.04}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white p-6 shadow-soft" data-testid={`benefit-${i}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal/10 text-teal"><b.icon size={20} /></span>
                  <h3 className="mt-4 font-heading text-sm font-semibold text-navy">{b.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-bodygrey">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* S12 — WHY PROXIMITY MATTERS */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-proximity">
        <div className="container-x section-space">
          <Reveal className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">Why proximity matters</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">The People Around You Already Matter</h2>
            <p className="mt-4 text-bodygrey">
              Every day we move past people we know almost nothing about. The person sitting nearby may share your
              interests. Someone at the same event may work in the industry you're trying to enter. Someone in your
              local community may also be looking for people to train, walk, network or socialise with.
            </p>
            <p className="mt-3 text-bodygrey">
              Traditional social platforms connect people across the world. Orrbbit also asks a different question:
              <strong className="text-navy"> Who is already around you?</strong>
            </p>
            <p className="mt-5 font-heading text-lg font-semibold text-teal">Real People. Real Moments. Right Nearby.</p>
          </Reveal>
        </div>
      </section>

      {/* EXISTING — HOW ORRBBIT HELPS */}
      <section className="bg-lightbg" data-testid="how-orrbbit-helps">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">How Orrbbit helps</h2>
            <p className="mt-4 text-bodygrey">Orrbbit is built around one idea: technology should lead people back into the real world.</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {orrbbitLinks.map((t) => (
              <div key={t} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-5">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal/10 text-teal"><Check size={16} /></span>
                <p className="text-sm text-navy">{t}</p>
              </div>
            ))}
          </div>
          <Reveal className="mt-6">
            <Link to="/how-orrbitt-uses-ai" data-testid="benefits-ai-learn-more" className="inline-flex items-center gap-1 font-semibold text-teal hover:underline">See how matching works <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>

      {/* S13 — WHAT RESEARCH HELPS US UNDERSTAND */}
      <section className="border-t border-border bg-white" data-testid="benefits-research-callout">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">The evidence</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">What Research Helps Us Understand</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="research-summary-cards">
            {researchSummaries.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-lightbg p-6 shadow-soft">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal/10 text-teal"><BookOpen size={20} /></span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-navy">{r.title}</h3>
                  <p className="mt-2 text-sm text-bodygrey">{r.body}</p>
                  <SourceLine>{r.ref}</SourceLine>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 max-w-3xl">
            <p className="rounded-2xl border border-orange/20 bg-orange/5 p-5 text-sm text-navy" data-testid="research-disclaimer">
              Research referenced on this page relates to human behaviour, social interaction and social connection
              generally. Orrbbit is not a treatment for anxiety or any mental-health condition.
            </p>
          </Reveal>

        </div>
      </section>

      {/* RESEARCH & SOURCES — grouped, every entry externally linked */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-sources">
        <div className="container-x section-space">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-teal">Transparency</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">Research &amp; Sources</h2>
            <p className="mt-3 text-bodygrey">
              Every statistic and research claim on this page is supported by the sources below, from the World Health
              Organization, the Australian Institute of Health and Welfare and peer-reviewed journals.
            </p>
          </Reveal>
          <div className="mt-10 space-y-10">
            {sourceGroups.map((g) => (
              <div key={g.title}>
                <Reveal>
                  <h3 className="font-heading text-lg font-semibold text-navy">{g.title}</h3>
                </Reveal>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid={`source-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {g.items.map((s, i) => (
                    <Reveal key={s.title} delay={i * 0.04}>
                      <div className="flex h-full flex-col rounded-2xl border border-border bg-white p-5 shadow-soft">
                        <p className="text-sm font-semibold text-navy">{s.title}</p>
                        <p className="mt-1 text-xs font-medium text-bodygrey">{s.org} · {s.year}</p>
                        <p className="mt-2 flex-1 text-sm text-bodygrey">{s.desc}</p>
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-teal hover:underline">
                          <ExternalLink size={14} /> View source
                        </a>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* S15 — CLOSING */}
      <section className="border-t border-border bg-lightbg" data-testid="benefits-closing-guess">
        <div className="container-x section-space">
          <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-navy md:text-4xl">What If You Didn't Have to Guess?</h2>
            <p className="mt-5 text-bodygrey">
              What if you already knew the person nearby was open to meeting someone? What if they already knew you
              were too?
            </p>
            <p className="mt-4 text-bodygrey">
              Orrbbit is built around that simple shift. From uncertainty to intention. From wondering to knowing.
              From being nearby to actually connecting.
            </p>
            <p className="mt-6 font-heading text-xl font-semibold text-navy">Technology creates the opportunity. People create the connection.</p>
            <p className="mt-2 font-heading text-lg font-bold text-teal">Real People. Real Moments. Right Nearby.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/how-it-works" data-testid="benefits-closing-how-btn" className="inline-flex items-center gap-2 rounded-full bg-teal px-7 py-3.5 font-semibold text-white shadow-soft transition-transform hover:scale-105">See How Orrbbit Works <ArrowRight size={18} /></Link>
              <Link to="/get-started" data-testid="benefits-closing-start-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-teal px-7 py-3.5 font-semibold text-teal transition-colors hover:bg-teal hover:text-white">Get Started</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ClosingCta photo="volunteers-tent" title="Connection is already around you." body="Orrbbit helps turn nearby moments into real human connection — one coffee, one event, one conversation at a time." cta={{ to: "/get-started", label: "Get Started" }} testid="benefits-closing-cta" />
    </div>
  );
}

